/**
 * E002-T018: Process Tab Component
 * E002-T037: Batching & Error Handling Config
 *
 * The automation's workflow settings for the settings panel.
 * `part="workflow"` renders the workflow selection (with the missing AI keys
 * notice); `part="failure"` renders retries and the on-failure mode.
 */

import { Switch } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { ViewIcon } from '@blocksuite/icons/rc';
import { useService, useServiceOptional } from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback } from 'react';

import { WorkspaceDialogService } from '../../dialogs';
import { EditorService } from '../../editor';
import type { AutomationConfig } from '../services/automation';
import {
  type BatchConfig,
  normalizeBatchConfig,
  type OnFailureMode,
} from '../services/execution';
import { TutorialRegistry } from '../tutorial/services/tutorial-registry';
import * as styles from './automation-panel.css';
import { CollapsibleSection } from './components/collapsible-section';
import { openWorkflowBuilder } from './components/open-workflow';
import {
  SectionControlRow,
  SectionField,
  SectionHint,
  SectionNotice,
} from './components/section-layout';
import { SettingSection } from './components/setting-section';
import { SettingSelect } from './components/setting-select';
import { WorkflowField } from './components/workflow-field';
import type { WorkflowSwitchDeps } from './workflow-switch-handler';

interface ProcessTabProps {
  /** `workflow`: workflow selection; `failure`: retries and on-failure mode. */
  part: 'workflow' | 'failure';
  readMappings: WorkflowSwitchDeps['readMappings'];
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** sl-kpg5.27: AI providers the selected workflow needs but lacks keys for. */
  missingAiKeys?: readonly string[];
}

export const ProcessTab: FC<ProcessTabProps> = ({
  part,
  readMappings,
  config,
  onUpdate,
  missingAiKeys = [],
}) => {
  const t = useI18n();
  const registry = useService(TutorialRegistry);

  const editor = useServiceOptional(EditorService)?.editor;
  const workspaceDialogService = useService(WorkspaceDialogService);

  // Get current batch config or defaults
  const batchConfig = normalizeBatchConfig(config.batchConfig);

  const handleBatchConfigChange = useCallback(
    (updates: Partial<BatchConfig>) => {
      onUpdate({
        batchConfig: normalizeBatchConfig({ ...batchConfig, ...updates }),
      });
    },
    [onUpdate, batchConfig]
  );

  const handleRetryToggle = useCallback(() => {
    handleBatchConfigChange({
      retry: { ...batchConfig.retry, enabled: !batchConfig.retry.enabled },
    });
  }, [handleBatchConfigChange, batchConfig.retry]);

  const onFailureOptions: { value: OnFailureMode; label: string }[] = [
    {
      value: 'stop',
      label: t['com.streamlain.streamlain.process.failure-stop'](),
    },
    {
      value: 'continue',
      label: t['com.streamlain.streamlain.process.failure-continue'](),
    },
  ];

  if (part === 'failure')
    return (
      <SettingSection tile="failure" section="advanced">
        <CollapsibleSection
          title={t['com.streamlain.automation.panel.tile.failure']()}
          subtitle={t['com.streamlain.streamlain.process.advanced-subtitle']()}
          onInfoClick={() => registry.start('automation:processWorkflow')}
        >
          <SectionControlRow
            label={t['com.streamlain.streamlain.process.retry-on-failure']()}
            coachmarkTarget="retry-toggle"
          >
            <Switch
              checked={batchConfig.retry.enabled}
              onChange={handleRetryToggle}
            />
          </SectionControlRow>

          {batchConfig.retry.enabled && (
            <SectionHint>
              {t['com.streamlain.streamlain.process.retry-info']({
                count: String(batchConfig.retry.maxAttempts),
              })}
            </SectionHint>
          )}

          <div data-coachmark-target="on-failure-menu">
            <SectionField
              label={t['com.streamlain.streamlain.process.on-failure']()}
            >
              <SettingSelect
                value={batchConfig.onFailure}
                options={onFailureOptions}
                onChange={onFailure => handleBatchConfigChange({ onFailure })}
                ariaLabel={t['com.streamlain.streamlain.process.on-failure']()}
                data-testid="automation-on-failure"
              />
            </SectionField>
          </div>
        </CollapsibleSection>
      </SettingSection>
    );

  return (
    <SettingSection
      tile="workflow"
      section="workflow-selector"
      headerActions={
        config.workflowId ? (
          <button
            type="button"
            onClick={() =>
              config.workflowId &&
              openWorkflowBuilder(
                editor,
                config.workflowId,
                config.workflowName ?? ''
              )
            }
            title={t['com.streamlain.streamlain.process.view-workflow']()}
            className={styles.viewWorkflowButton}
          >
            <ViewIcon width={14} height={14} />
            {t['com.streamlain.streamlain.common.view']()}
          </button>
        ) : null
      }
    >
      <WorkflowField
        config={config}
        onUpdate={onUpdate}
        readMappings={readMappings}
      />

      {/* sl-kpg5.27: AI Key Warning */}
      {missingAiKeys.length > 0 && (
        <SectionNotice variant="warning">
          <span>
            <strong>
              {t['com.streamlain.streamlain.process.missing-ai-keys']()}
            </strong>{' '}
            {t['com.streamlain.streamlain.process.missing-ai-keys-detail']({
              keys: missingAiKeys.join(', '),
            })}{' '}
            <button
              type="button"
              className={styles.viewWorkflowButton}
              onClick={() =>
                workspaceDialogService.open('setting', {
                  activeTab: 'workspace:ai',
                })
              }
            >
              {t['com.streamlain.streamlain.execution.open-ai-settings']()}
            </button>
          </span>
        </SectionNotice>
      )}
    </SettingSection>
  );
};
